import { useEffect, useRef, useState, useCallback } from 'react'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

export type StatoSessione =
  | 'inattiva'           // non avviata
  | 'inizializzazione'   // richiesta permesso microfono + connessione WS
  | 'pronto'             // lead pronto, in attesa che l'utente parli
  | 'ascolto'            // microfono attivo
  | 'elaborazione'       // Claude sta pensando
  | 'lead_parla'         // ElevenLabs sta riproducendo
  | 'completata'         // sessione terminata, feedback salvato
  | 'errore'

interface RispostaCreaSessione {
  session_id: string
  ws_url: string
}

interface UseSessioneVoceReturn {
  stato: StatoSessione
  trascrizione: string         // ultimo turno SDR (parziale o finale)
  rispostaLead: string         // ultimo turno lead
  volumeMicrofono: number      // 0..1 per la waveform
  sessionId: string | null     // id della sessione, disponibile dopo `avvia`
  errore: string | null
  /**
   * True quando la sessione è completata ma il feedback Claude è ancora in
   * elaborazione in background sul server. Il frontend lo usa per mostrare
   * "Feedback in elaborazione…" invece di "Vai al feedback".
   */
  feedbackInBackground: boolean
  /** Messaggio descrittivo della chiusura (es. "Sessione salvata…"). */
  messaggioChiusura: string | null
  avvia: () => Promise<void>
  termina: () => void
  /**
   * Forza l'invio della frase accumulata sul backend senza aspettare
   * i VOICE_SILENZIO_MS di silenzio (4s di default). Usato dal bottone
   * "Invia frase" quando l'utente ha finito di parlare e non vuole
   * aspettare la finestra automatica.
   */
  forzaTurno: () => void
}

/**
 * Hook che gestisce una sessione vocale via browser.
 *
 * Pipeline lato client:
 *  1. Chiama POST /voice/sessione → riceve session_id + ws_url
 *  2. Chiede permesso microfono (getUserMedia)
 *  3. Apre WebSocket verso il backend
 *  4. AudioContext + ScriptProcessor catturano Float32 → convertono in Int16
 *     PCM 16kHz mono → mandano via WS binario
 *  5. AnalyserNode calcola il volume del mic per la waveform
 *  6. Riceve messaggi JSON dal server: trascrizione, risposta lead, audio MP3
 *  7. Quando arriva 'fine_audio', accumula gli MP3 in un Blob, lo decodifica
 *     con AudioContext.decodeAudioData e lo riproduce
 *  8. `termina()` manda { tipo: 'fine' } al server e chiude tutto
 */
export function useSessioneVoce(leadProfileId: string, obiettivoScelto?: string): UseSessioneVoceReturn {
  const token = useAuthStore((s) => s.token)

  const [stato, setStato] = useState<StatoSessione>('inattiva')
  const [trascrizione, setTrascrizione] = useState('')
  const [rispostaLead, setRispostaLead] = useState('')
  const [volumeMicrofono, setVolumeMicrofono] = useState(0)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [feedbackInBackground, setFeedbackInBackground] = useState(false)
  const [messaggioChiusura, setMessaggioChiusura] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const rafRef = useRef<number | null>(null)
  const audioChunksRef = useRef<Uint8Array[]>([])
  const audioElRef = useRef<HTMLAudioElement | null>(null)
  /** Timeout di sicurezza sull'init; cancellato all'arrivo di 'pronto'/'errore' */
  const timeoutInitRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * Quando true, lo ScriptProcessor non manda audio al backend.
   * Lo attiviamo quando il lead sta parlando o Claude sta elaborando, per:
   *  - evitare che l'audio del lead (riprodotto dagli speaker) rientri nel
   *    microfono e venga ri-trascritto come "voce dell'SDR"
   *  - non sprecare crediti Deepgram trascrivendo silenzio/eco
   */
  const pauseAudioSendingRef = useRef(false)

  const cleanup = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (processorRef.current) {
      try { processorRef.current.disconnect() } catch {/* ignora */}
      processorRef.current = null
    }
    if (analyserRef.current) {
      try { analyserRef.current.disconnect() } catch {/* ignora */}
      analyserRef.current = null
    }
    if (sourceRef.current) {
      try { sourceRef.current.disconnect() } catch {/* ignora */}
      sourceRef.current = null
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close() } catch {/* ignora */}
      audioCtxRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (wsRef.current) {
      try { wsRef.current.close() } catch {/* ignora */}
      wsRef.current = null
    }
    if (audioElRef.current) {
      try { audioElRef.current.pause() } catch {/* ignora */}
      audioElRef.current = null
    }
    if (timeoutInitRef.current) {
      clearTimeout(timeoutInitRef.current)
      timeoutInitRef.current = null
    }
    audioChunksRef.current = []
  }, [])

  const termina = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try { wsRef.current.send(JSON.stringify({ tipo: 'fine' })) } catch {/* ignora */}
    }
    // Lascia un attimo al backend per salvare il feedback prima di chiudere
    setTimeout(() => cleanup(), 500)
  }, [cleanup])

  const forzaTurno = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try { wsRef.current.send(JSON.stringify({ tipo: 'turno_completato' })) } catch {/* ignora */}
    }
  }, [])

  // Cleanup automatico allo smontaggio
  useEffect(() => () => cleanup(), [cleanup])

  const riproduciAudioBuffer = useCallback(async () => {
    if (audioChunksRef.current.length === 0) {
      pauseAudioSendingRef.current = false
      setStato('ascolto')
      return
    }

    // Concatena i chunk MP3 ricevuti e riproduce con un elemento Audio
    const totale = audioChunksRef.current.reduce((s, c) => s + c.length, 0)
    const merged = new Uint8Array(totale)
    let offset = 0
    for (const c of audioChunksRef.current) {
      merged.set(c, offset)
      offset += c.length
    }
    audioChunksRef.current = []

    const blob = new Blob([merged.buffer], { type: 'audio/mpeg' })
    const url = URL.createObjectURL(blob)
    const audio = new Audio(url)
    audioElRef.current = audio
    // Quando l'audio del lead finisce, ripristina lo stato per la
    // prossima frase: riprendi mic, pulisci trascrizione e risposta
    // del turno precedente.
    const onFine = () => {
      URL.revokeObjectURL(url)
      audioElRef.current = null
      pauseAudioSendingRef.current = false
      setTrascrizione('')
      setRispostaLead('')
      setStato('ascolto')
    }
    audio.onended = onFine
    audio.onerror = onFine
    setStato('lead_parla')
    try {
      await audio.play()
    } catch (err) {
      console.error('Errore riproduzione audio', err)
      pauseAudioSendingRef.current = false
      setStato('ascolto')
    }
  }, [])

  const avvia = useCallback(async () => {
    setErrore(null)
    setTrascrizione('')
    setRispostaLead('')
    setStato('inizializzazione')

    if (!token) {
      setErrore('Sessione non autenticata')
      setStato('errore')
      return
    }

    // 1. Crea la sessione lato backend
    let sessione: RispostaCreaSessione
    try {
      const body: { lead_profile_id: string; obiettivo_scelto?: string } = {
        lead_profile_id: leadProfileId,
      }
      if (obiettivoScelto?.trim()) body.obiettivo_scelto = obiettivoScelto.trim()
      const res = await api.post<{ dati: RispostaCreaSessione }>(
        '/voice/sessione',
        body,
      )
      const dati = res.data.dati
      if (!dati) throw new Error('Risposta vuota')
      sessione = dati
      setSessionId(dati.session_id)
    } catch (err) {
      const data = (err as { response?: { data?: { errore?: string } } }).response?.data
      setErrore(data?.errore ?? 'Impossibile creare la sessione')
      setStato('errore')
      return
    }

    // 2. Permesso microfono
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      streamRef.current = stream
    } catch {
      setErrore("Devi autorizzare l'accesso al microfono per usare questa funzione.")
      setStato('errore')
      return
    }

    // 3. AudioContext — creato PRIMA del WebSocket per conoscere il sample
    //    rate effettivo. NON forziamo `sampleRate: 16000`: alcuni
    //    browser/OS lanciano un errore se quel rate non è supportato
    //    nativamente. Lasciamo decidere al browser e mandiamo al backend
    //    il rate reale così Deepgram interpreta correttamente i sample.
    const AudioCtor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtor) {
      setErrore('AudioContext non disponibile in questo browser')
      setStato('errore')
      return
    }
    let audioCtx: AudioContext
    try {
      audioCtx = new AudioCtor()
    } catch (err) {
      console.error('[voice] AudioContext creation failed', err)
      setErrore('Impossibile inizializzare l\'audio. Prova a ricaricare la pagina.')
      setStato('errore')
      return
    }
    audioCtxRef.current = audioCtx
    const sampleRateReale = audioCtx.sampleRate
    console.log('[voice] AudioContext sample rate:', sampleRateReale)

    // 4. WebSocket — passo il sample rate in QUERY STRING (non in un
    //    messaggio JSON). Motivo: c'era una race condition con l'audio del
    //    ScriptProcessor, che arrivava al backend prima del messaggio 'config'
    //    perché la callback ws.onopen viene schedulata sull'event loop mentre
    //    onaudioprocess viene chiamato a priorità audio. La query è leggibile
    //    dal backend prima ancora di accettare la WS.
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const sep = sessione.ws_url.includes('?') ? '&' : '?'
    const wsUrl = `${proto}//${window.location.host}${sessione.ws_url}${sep}sample_rate=${sampleRateReale}`
    console.log('[voice] apertura WS:', wsUrl.replace(/token=[^&]+/, 'token=***'))
    const ws = new WebSocket(wsUrl)
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    // Timeout di sicurezza: se entro 12s non riceviamo 'pronto' o 'errore'
    // dal backend, segnaliamo errore e permettiamo all'utente di uscire.
    timeoutInitRef.current = setTimeout(() => {
      setStato((s) => {
        if (s === 'inizializzazione') {
          setErrore('Impossibile avviare la sessione (timeout). Riprova.')
          try { ws.close() } catch {/* ignora */}
          return 'errore'
        }
        return s
      })
    }, 12_000)

    ws.onopen = () => {
      // Nessun config da mandare: il sample rate è in query string.
      console.log('[voice] WS aperto')
    }

    ws.onmessage = (event) => {
      let msg: { tipo: string;[k: string]: unknown }
      try {
        msg = JSON.parse(event.data)
      } catch (err) {
        console.warn('[voice] msg non parsabile', event.data, err)
        return
      }
      console.log('[voice] msg ricevuto:', msg.tipo)

      switch (msg.tipo) {
        case 'pronto':
          if (timeoutInitRef.current) {
            clearTimeout(timeoutInitRef.current)
            timeoutInitRef.current = null
          }
          pauseAudioSendingRef.current = false
          setStato('ascolto')
          break
        case 'trascrizione': {
          // Mostra trascrizione parziale o finale in tempo reale.
          // Il cambio di stato avviene esplicitamente con il messaggio
          // 'elaborazione' del backend, non più inferito da `finale`.
          const t = msg.testo as string
          setTrascrizione(t)
          break
        }
        case 'elaborazione':
          // Claude sta pensando → ferma mic + indicatore visivo
          pauseAudioSendingRef.current = true
          setStato('elaborazione')
          break
        case 'risposta_testo':
          // Il lead sta per parlare → typewriter del testo
          pauseAudioSendingRef.current = true
          setRispostaLead(msg.testo as string)
          break
        case 'inizio_audio':
          pauseAudioSendingRef.current = true
          audioChunksRef.current = []
          setStato('lead_parla')
          break
        case 'audio': {
          const b64 = msg.data as string
          const bin = atob(b64)
          const arr = new Uint8Array(bin.length)
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i)
          audioChunksRef.current.push(arr)
          break
        }
        case 'fine_audio':
          void riproduciAudioBuffer()
          break
        case 'sessione_completata':
          setStato('completata')
          // Il backend invia feedback_in_background=true quando il job
          // Claude è stato lanciato e impiegherà tempo a finire. La UI
          // mostrerà "Feedback in elaborazione…" invece di "Vai al feedback".
          if (msg.feedback_in_background) {
            setFeedbackInBackground(true)
          }
          if (typeof msg.messaggio === 'string') {
            setMessaggioChiusura(msg.messaggio)
          }
          cleanup()
          break
        case 'errore':
          if (timeoutInitRef.current) {
            clearTimeout(timeoutInitRef.current)
            timeoutInitRef.current = null
          }
          setErrore((msg.messaggio as string) ?? 'Errore sconosciuto')
          setStato('errore')
          cleanup()
          break
        case 'pong':
          // keepalive ack
          break
      }
    }

    ws.onerror = (e) => {
      console.error('[voice] WS error', e)
      setErrore('Errore di connessione al server vocale')
      setStato('errore')
    }

    ws.onclose = (e) => {
      console.log('[voice] WS chiuso', { code: e.code, reason: e.reason, wasClean: e.wasClean })
      setStato((s) => (s !== 'completata' && s !== 'errore' ? 'completata' : s))
    }

    // 5. Audio graph (source + analyser + processor)
    const source = audioCtx.createMediaStreamSource(stream)
    sourceRef.current = source

    // Analyser per la waveform
    const analyser = audioCtx.createAnalyser()
    analyser.fftSize = 64
    source.connect(analyser)
    analyserRef.current = analyser

    const volumeData = new Uint8Array(analyser.frequencyBinCount)
    const tickVolume = () => {
      analyser.getByteFrequencyData(volumeData)
      const sum = volumeData.reduce((s, v) => s + v, 0)
      const avg = sum / volumeData.length / 255
      setVolumeMicrofono(avg)
      rafRef.current = requestAnimationFrame(tickVolume)
    }
    tickVolume()

    // ScriptProcessor: deprecato ma supportato ovunque.
    // Per produzione ideale usare AudioWorklet; qui privilegiamo semplicità.
    const processor = audioCtx.createScriptProcessor(4096, 1, 1)
    processorRef.current = processor
    processor.onaudioprocess = (e) => {
      if (ws.readyState !== WebSocket.OPEN) return
      // Salta l'invio mentre il lead sta parlando o Claude sta elaborando:
      // evita echo capture + risparmia crediti Deepgram.
      if (pauseAudioSendingRef.current) return
      const input = e.inputBuffer.getChannelData(0)
      const int16 = new Int16Array(input.length)
      for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i] ?? 0))
        int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      }
      try { ws.send(int16.buffer) } catch {/* ignora */}
    }
    source.connect(processor)
    // Necessario per attivare l'audioprocess in alcuni browser
    processor.connect(audioCtx.destination)

    // 6. Keepalive ping ogni 25s
    const pingId = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ tipo: 'ping' })) } catch {/* ignora */}
      } else {
        clearInterval(pingId)
      }
    }, 25_000)
  }, [leadProfileId, obiettivoScelto, token, riproduciAudioBuffer, cleanup])

  return {
    stato,
    trascrizione,
    rispostaLead,
    volumeMicrofono,
    sessionId,
    errore,
    feedbackInBackground,
    messaggioChiusura,
    avvia,
    termina,
    forzaTurno,
  }
}
